import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import Box from '@mui/material/Box';
import React, { memo, useMemo } from 'react';
import { Helmet } from 'react-helmet';

import { logError } from '../../util/log';

// Memoize default style to prevent recreation on every render
const defaultStyle = {
  objectFit: 'cover',
  objectPosition: 'center center',
  width: '100%',
  height: '100%',
};

const ProgressiveImageComponent = ({
  addHelmet = true,
  disableBlur = false,
  loadLazy = true,
  title = '',
  src = null,
  blurSrc = null,
  srcset = '',
  sizes = '',
  alt = '',
  height = '100%',
  width = '100%',
  imageStyle = {},
  setNaturalSize = (params) => null,
  useNaturalSize = false,
  loadingStyle,
}) => {
  // Memoize styles to prevent recreation on every render
  const mergedStyles = useMemo(() => ({ ...defaultStyle, ...imageStyle }), [imageStyle]);

  // The preload has to describe the SAME candidate set as the <img>, otherwise the
  // browser preloads `src` and then downloads whatever the srcset picks — two
  // requests, and the one that actually paints the LCP starts late.
  // `imagesrcset`/`imagesizes` are the preload counterparts of srcset/sizes;
  // `href` stays as the fallback for browsers that don't support them.
  const preloadLink = useMemo(() => {
    const link = { rel: 'preload', as: 'image', href: src, fetchpriority: 'high' };

    if (srcset) {
      link.imagesrcset = srcset;
      // A `w`-descriptor srcset without sizes falls back to 100vw. Spelling it out
      // keeps the preload's candidate selection identical to the <img>'s.
      link.imagesizes = sizes || '100vw';
    }

    return [link];
  }, [src, srcset, sizes]);

  // Handle natural size updates
  const handleNaturalSize = (img) => {
    if (!useNaturalSize) return;

    try {
      if (img && typeof img === 'object' && 'naturalWidth' in img && 'naturalHeight' in img) {
        setNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    } catch (e) {
      logError(e, 'handle-natural-size');
    }
  };

  // Early return if no images are available
  if (!src && !blurSrc) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: 'grey.300',
        }}
      >
        <ImageNotSupportedIcon />
      </Box>
    );
  }

  const imgStyles = {
    maxWidth: '100%',
    ...mergedStyles,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        maxWidth: '100%',
        maxHeight: '100%',
      }}
    >
      {addHelmet && !loadLazy && src ? <Helmet link={preloadLink} /> : null}
      <img
        alt={alt}
        fetchpriority={loadLazy ? undefined : 'high'}
        height={height}
        loading={loadingStyle ? loadingStyle : loadLazy ? 'lazy' : 'eager'}
        onLoad={useNaturalSize ? (e) => handleNaturalSize(e.target) : undefined}
        sizes={sizes || undefined}
        src={src}
        srcSet={srcset}
        style={imgStyles}
        title={title}
        width={width}
      />
    </Box>
  );
};

ProgressiveImageComponent.displayName = 'ProgressiveImage';

const ProgressiveImage = memo(ProgressiveImageComponent);

export default ProgressiveImage;